import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import {
  Stakeholder, StakeholderEngagement, InfluenceLevel, EngagementType, Project,
} from '../../../types';
import {
  getProjectStakeholders, createStakeholder, updateStakeholder, deleteStakeholder,
  getStakeholderEngagements, createEngagement, deleteEngagement,
} from '../../../services/stakeholderService';
import PeoplePicker, { Person } from '../../../components/PeoplePicker';
import ModuleGuard from '../../../components/ModuleGuard';
import Badge from '../../../components/Badge';
import AutoSizingTextarea from '../../../components/AutoSizingTextarea';
import BoardModal, {
  BMCancelButton, BMPrimaryButton, BMDangerButton,
  BM_LABEL, BM_INPUT, BM_TITLE_INPUT,
} from '../../../components/BoardModal';
import { useCurrentUser } from '../../../hooks/useCurrentUser';

interface Props { pca: IPublicClientApplication; projectId: string; project: Project; }

const LEVEL_LABEL: Record<number, string> = {
  [InfluenceLevel.Low]: 'Low', [InfluenceLevel.Medium]: 'Medium', [InfluenceLevel.High]: 'High',
};
const LEVEL_BADGE: Record<number, { bg: string; fg: string }> = {
  [InfluenceLevel.Low]:    { bg: '#F3F4F6', fg: '#5A6272' },
  [InfluenceLevel.Medium]: { bg: '#FEF3C7', fg: '#92400E' },
  [InfluenceLevel.High]:   { bg: '#FEE2E2', fg: '#991B1B' },
};

const ENGAGE_TYPE_LABEL: Record<number, string> = {
  [EngagementType.Meeting]: 'Meeting', [EngagementType.Email]: 'Email',
  [EngagementType.Call]: 'Call', [EngagementType.Report]: 'Report',
  [EngagementType.Other]: 'Other',
};

// Power-interest quadrant classification per the Mendelow grid:
// high infl + high interest = manage closely (red, highest attention)
// high infl + low interest  = keep satisfied (amber)
// low infl  + high interest = keep informed (blue)
// low infl  + low interest  = monitor (gray)
// medium/medium is treated as yellow (transitional).
function quadrantColor(influence?: number | null, interest?: number | null): string {
  const i = influence ?? 0, t = interest ?? 0;
  if (i === InfluenceLevel.High && t === InfluenceLevel.High) return '#DC2626'; // red
  if (i === InfluenceLevel.High && t === InfluenceLevel.Low)  return '#D97706'; // amber
  if (i === InfluenceLevel.Low  && t === InfluenceLevel.High) return '#2563EB'; // blue
  if (i === InfluenceLevel.Low  && t === InfluenceLevel.Low)  return '#9198A8'; // gray
  // Any medium involvement → yellow
  return '#EAB308';
}

function quadrantLabel(influence?: number | null, interest?: number | null): string {
  const i = influence ?? 0, t = interest ?? 0;
  if (i === InfluenceLevel.High && t === InfluenceLevel.High) return 'Manage Closely';
  if (i === InfluenceLevel.High && t === InfluenceLevel.Low)  return 'Keep Satisfied';
  if (i === InfluenceLevel.Low  && t === InfluenceLevel.High) return 'Keep Informed';
  if (i === InfluenceLevel.Low  && t === InfluenceLevel.Low)  return 'Monitor';
  return 'Mixed — watch for shifts';
}

export default function StakeholderTab(props: Props) {
  return (
    <ModuleGuard project={props.project} flag="dagrun_pm_module_stakeholders">
      <StakeholderTabBody {...props} />
    </ModuleGuard>
  );
}

function StakeholderTabBody({ pca, projectId }: Props) {
  const [items, setItems] = useState<Stakeholder[]>([]);
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState<Stakeholder | null>(null);

  const reload = () => getProjectStakeholders(pca, projectId).then(setItems);
  useEffect(() => { reload(); }, [pca, projectId]); // eslint-disable-line

  return (
    <div style={{ padding: '20px 32px', fontFamily: 'var(--font-ui)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#0F1117' }}>Stakeholder Register</h3>
        <button type="button" onClick={() => setShowNew(true)}
          style={{ background: '#0E7490', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 6, fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          onMouseEnter={e => (e.currentTarget.style.background = '#0A5568')}
          onMouseLeave={e => (e.currentTarget.style.background = '#0E7490')}
        >+ Add Stakeholder</button>
      </div>

      <div style={{ fontSize: 12, color: '#5A6272', marginBottom: 14, lineHeight: 1.5 }}>
        Dot color reflects the Mendelow power/interest grid:{' '}
        <QuadrantDot color="#DC2626" /> Manage closely{'  '}
        <QuadrantDot color="#D97706" /> Keep satisfied{'  '}
        <QuadrantDot color="#2563EB" /> Keep informed{'  '}
        <QuadrantDot color="#9198A8" /> Monitor
      </div>

      {items.length === 0 ? (
        <div style={{ padding: 48, textAlign: 'center', color: '#9198A8', fontSize: 14 }}>
          No stakeholders registered yet.
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ borderBottom: '1px solid #DDE0E6', textAlign: 'left' }}>
            <Th>Name</Th><Th>Role</Th><Th>Influence</Th><Th>Interest</Th><Th>Engagement Strategy</Th>
          </tr></thead>
          <tbody>
            {items.map(s => (
              <tr key={s.dagrun_pm_stakeholderid} onClick={() => setSelected(s)}
                style={{ borderBottom: '0.5px solid #EAECF0', cursor: 'pointer', transition: 'background 0.12s' }}
                onMouseEnter={e => (e.currentTarget.style.background = '#F5F6F8')}
                onMouseLeave={e => (e.currentTarget.style.background = '')}>
                <Td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <QuadrantDot color={quadrantColor(s.dagrun_pm_influence, s.dagrun_pm_interest)} />
                    <span style={{ color: '#0F1117', fontSize: 14 }}>{s.dagrun_pm_name}</span>
                  </div>
                </Td>
                <Td><span style={{ fontSize: 13, color: '#5A6272' }}>{s.dagrun_pm_role ?? <Em />}</span></Td>
                <Td>{s.dagrun_pm_influence != null
                  ? <Badge {...LEVEL_BADGE[s.dagrun_pm_influence]}>{LEVEL_LABEL[s.dagrun_pm_influence]}</Badge>
                  : <Em />}</Td>
                <Td>{s.dagrun_pm_interest != null
                  ? <Badge {...LEVEL_BADGE[s.dagrun_pm_interest]}>{LEVEL_LABEL[s.dagrun_pm_interest]}</Badge>
                  : <Em />}</Td>
                <Td><span title={s.dagrun_pm_engagementstrategy ?? ''} style={{
                  fontSize: 12, color: '#5A6272',
                  display: 'inline-block', maxWidth: 280,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  verticalAlign: 'middle',
                }}>{s.dagrun_pm_engagementstrategy ?? <Em />}</span></Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showNew && (
        <CreateStakeholderModal pca={pca} projectId={projectId}
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); reload(); }}
        />
      )}
      {selected && (
        <StakeholderDrawer pca={pca} stakeholder={selected} projectId={projectId}
          onClose={() => { setSelected(null); reload(); }}
          onDeleted={() => { setSelected(null); reload(); }}
        />
      )}
    </div>
  );
}

const Th = ({ children }: { children: React.ReactNode }) => (
  <th style={{ padding: '8px 12px', fontWeight: 500, fontSize: 12, color: '#5A6272', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{children}</th>
);
const Td = ({ children }: { children: React.ReactNode }) => (
  <td style={{ padding: '10px 12px', verticalAlign: 'middle' }}>{children}</td>
);
const Em = () => <span style={{ color: '#B8BEC9', fontSize: 13 }}>—</span>;

function QuadrantDot({ color }: { color: string }) {
  return <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: color, flexShrink: 0 }} />;
}

function CreateStakeholderModal({ pca, projectId, onClose, onCreated }: {
  pca: IPublicClientApplication; projectId: string; onClose: () => void; onCreated: () => void;
}) {
  const [person, setPerson] = useState<Person | null>(null);
  const [nameOverride, setNameOverride] = useState('');
  const [role, setRole] = useState('');
  const [influence, setInfluence] = useState<number>(InfluenceLevel.Medium);
  const [interest, setInterest] = useState<number>(InfluenceLevel.Medium);
  const [strategy, setStrategy] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const effectiveName = person?.displayName ?? nameOverride.trim();

  const submit = async () => {
    if (!effectiveName || saving) return;
    setSaving(true); setError(null);
    try {
      await createStakeholder(pca, {
        projectId,
        name: effectiveName,
        userid: person?.aadObjectId ?? null,
        username: person?.displayName ?? null,
        role: role.trim() || null,
        influence, interest,
        engagementstrategy: strategy.trim() || null,
        notes: notes.trim() || null,
      });
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed');
    } finally { setSaving(false); }
  };

  return (
    <BoardModal open title="Add Stakeholder" onClose={onClose} width={560}
      footer={<>
        <BMCancelButton onClick={onClose} disabled={saving} />
        <BMPrimaryButton onClick={submit} disabled={saving || !effectiveName}>
          {saving ? 'Creating…' : 'Add Stakeholder'}
        </BMPrimaryButton>
      </>}>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Person (search directory)</label>
        <PeoplePicker pca={pca} value={person} onChange={setPerson} placeholder="Search users…" />
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Or enter name (external stakeholder)</label>
        <input value={nameOverride} onChange={e => setNameOverride(e.target.value)}
          style={BM_TITLE_INPUT} placeholder="External contact name" disabled={!!person} />
        {person && (
          <div style={{ fontSize: 11, color: '#9198A8', marginTop: 4 }}>Using directory lookup — clear to type a name manually.</div>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
        <div>
          <label style={BM_LABEL}>Role</label>
          <input value={role} onChange={e => setRole(e.target.value)} style={BM_INPUT} placeholder="e.g. Executive Sponsor" />
        </div>
        <div />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
        <div>
          <label style={BM_LABEL}>Influence</label>
          <select value={influence} onChange={e => setInfluence(Number(e.target.value))} style={BM_INPUT}>
            {[InfluenceLevel.Low, InfluenceLevel.Medium, InfluenceLevel.High].map(v =>
              <option key={v} value={v}>{LEVEL_LABEL[v]}</option>)}
          </select>
        </div>
        <div>
          <label style={BM_LABEL}>Interest</label>
          <select value={interest} onChange={e => setInterest(Number(e.target.value))} style={BM_INPUT}>
            {[InfluenceLevel.Low, InfluenceLevel.Medium, InfluenceLevel.High].map(v =>
              <option key={v} value={v}>{LEVEL_LABEL[v]}</option>)}
          </select>
        </div>
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Engagement Strategy</label>
        <AutoSizingTextarea value={strategy} onChange={setStrategy} />
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Notes</label>
        <AutoSizingTextarea value={notes} onChange={setNotes} />
      </div>
      <div style={{ padding: '8px 10px', background: '#F5F6F8', borderRadius: 6, fontSize: 12, color: '#5A6272' }}>
        Quadrant: <strong style={{ color: quadrantColor(influence, interest) }}>{quadrantLabel(influence, interest)}</strong>
      </div>
      {error && <div style={{ marginTop: 12, color: '#DC2626', fontSize: 12 }}>{error}</div>}
    </BoardModal>
  );
}

function StakeholderDrawer({ pca, stakeholder, projectId: _projectId, onClose, onDeleted }: {
  pca: IPublicClientApplication; stakeholder: Stakeholder; projectId: string;
  onClose: () => void; onDeleted: () => void;
}) {
  const [draft, setDraft] = useState<Stakeholder>(stakeholder);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const flashTimer = useRef<number | null>(null);
  const textDebounce = useRef<number | null>(null);

  useEffect(() => { setDraft(stakeholder); }, [stakeholder]);

  const patch = async (fields: Partial<Stakeholder>) => {
    setSaving(true);
    try {
      await updateStakeholder(pca, draft.dagrun_pm_stakeholderid, fields as Record<string, unknown>);
      setSavedFlash(true);
      if (flashTimer.current) window.clearTimeout(flashTimer.current);
      flashTimer.current = window.setTimeout(() => setSavedFlash(false), 1500);
    } finally { setSaving(false); }
  };
  const setAndPatch = <K extends keyof Stakeholder>(k: K, v: Stakeholder[K]) => {
    setDraft(d => ({ ...d, [k]: v })); patch({ [k]: v } as Partial<Stakeholder>);
  };
  const setText = <K extends keyof Stakeholder>(k: K, v: Stakeholder[K]) => {
    setDraft(d => ({ ...d, [k]: v }));
    if (textDebounce.current) window.clearTimeout(textDebounce.current);
    textDebounce.current = window.setTimeout(() => patch({ [k]: v } as Partial<Stakeholder>), 600);
  };
  const flush = <K extends keyof Stakeholder>(k: K, v: Stakeholder[K]) => {
    if (textDebounce.current) window.clearTimeout(textDebounce.current);
    patch({ [k]: v } as Partial<Stakeholder>);
  };
  const doDelete = async () => {
    setSaving(true);
    try { await deleteStakeholder(pca, draft.dagrun_pm_stakeholderid); onDeleted(); } finally { setSaving(false); }
  };

  const quadColor = quadrantColor(draft.dagrun_pm_influence, draft.dagrun_pm_interest);
  const quadText  = quadrantLabel(draft.dagrun_pm_influence, draft.dagrun_pm_interest);

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,17,23,0.18)', zIndex: 100 }} />
      <div style={{
        position: 'fixed', top: 0, right: 0, height: '100%', width: 520,
        background: '#fff', borderLeft: '0.5px solid #EAECF0',
        boxShadow: '0 4px 24px rgba(15,17,23,0.12)', zIndex: 101,
        overflowY: 'auto', padding: '16px 24px 32px 24px',
        fontFamily: 'var(--font-ui)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <span style={{ fontSize: 11, color: '#9198A8', opacity: savedFlash ? 1 : 0, transition: 'opacity 0.2s' }}>
            {savedFlash ? 'Saved ✓' : ''}
          </span>
          <button onClick={onClose} aria-label="Close" style={{ width: 28, height: 28, border: 'none', background: 'transparent', cursor: 'pointer', color: '#5A6272', fontSize: 18 }}>×</button>
        </div>

        <input value={draft.dagrun_pm_name}
          onChange={e => setText('dagrun_pm_name', e.target.value)}
          onBlur={e => flush('dagrun_pm_name', e.target.value)}
          style={{ fontSize: 18, fontWeight: 600, color: '#0F1117', border: 'none', padding: 0, marginBottom: 8, width: '100%', background: 'transparent' }} />

        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '4px 10px', marginBottom: 16,
          background: `${quadColor}22`, color: quadColor,
          borderRadius: 12, fontSize: 11, fontWeight: 600,
        }}>
          <QuadrantDot color={quadColor} />
          <span>{quadText}</span>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={BM_LABEL}>Role</label>
          <input value={draft.dagrun_pm_role ?? ''}
            onChange={e => setText('dagrun_pm_role', e.target.value || null)}
            onBlur={e => flush('dagrun_pm_role', e.target.value || null)} style={BM_INPUT} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={BM_LABEL}>Influence</label>
            <select value={draft.dagrun_pm_influence ?? InfluenceLevel.Medium}
              onChange={e => setAndPatch('dagrun_pm_influence', Number(e.target.value))} style={BM_INPUT}>
              {[InfluenceLevel.Low, InfluenceLevel.Medium, InfluenceLevel.High].map(v =>
                <option key={v} value={v}>{LEVEL_LABEL[v]}</option>)}
            </select>
          </div>
          <div>
            <label style={BM_LABEL}>Interest</label>
            <select value={draft.dagrun_pm_interest ?? InfluenceLevel.Medium}
              onChange={e => setAndPatch('dagrun_pm_interest', Number(e.target.value))} style={BM_INPUT}>
              {[InfluenceLevel.Low, InfluenceLevel.Medium, InfluenceLevel.High].map(v =>
                <option key={v} value={v}>{LEVEL_LABEL[v]}</option>)}
            </select>
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={BM_LABEL}>Engagement Strategy</label>
          <AutoSizingTextarea
            value={draft.dagrun_pm_engagementstrategy ?? ''}
            onChange={v => setText('dagrun_pm_engagementstrategy', v || null)}
            onBlur={v => flush('dagrun_pm_engagementstrategy', v || null)}
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={BM_LABEL}>Notes</label>
          <AutoSizingTextarea
            value={draft.dagrun_pm_notes ?? ''}
            onChange={v => setText('dagrun_pm_notes', v || null)}
            onBlur={v => flush('dagrun_pm_notes', v || null)}
          />
        </div>

        <EngagementLogSection pca={pca} stakeholderId={draft.dagrun_pm_stakeholderid} />

        <div style={{ marginTop: 28, display: 'flex', justifyContent: 'flex-end' }}>
          <BMDangerButton onClick={() => setConfirmDelete(true)}>Delete</BMDangerButton>
        </div>
      </div>

      {confirmDelete && (
        <BoardModal open title="Delete Stakeholder" onClose={() => setConfirmDelete(false)}
          footer={<>
            <BMCancelButton onClick={() => setConfirmDelete(false)} disabled={saving} />
            <BMDangerButton onClick={doDelete} disabled={saving}>{saving ? 'Deleting…' : 'Delete'}</BMDangerButton>
          </>}>
          Delete <strong style={{ color: '#0F1117' }}>{draft.dagrun_pm_name}</strong>? Engagement history will also be orphaned.
        </BoardModal>
      )}
    </>
  );
}

function EngagementLogSection({ pca, stakeholderId }: { pca: IPublicClientApplication; stakeholderId: string }) {
  const me = useCurrentUser(pca, '');
  const [items, setItems] = useState<StakeholderEngagement[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [date, setDate] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [type, setType] = useState<number>(EngagementType.Meeting);
  const [summary, setSummary] = useState('');
  const [loggedBy, setLoggedBy] = useState<Person | null>(null);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    try {
      setLoading(true);
      setItems(await getStakeholderEngagements(pca, stakeholderId));
    } finally { setLoading(false); }
  }, [pca, stakeholderId]);
  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    if (me && !loggedBy) setLoggedBy({ aadObjectId: me.oid, displayName: me.displayName });
  }, [me, loggedBy]);

  const cancelAdd = () => {
    setAdding(false);
    setDate(new Date().toISOString().slice(0, 10));
    setType(EngagementType.Meeting);
    setSummary('');
  };

  const save = async () => {
    if (!loggedBy || !date || saving) return;
    setSaving(true);
    try {
      await createEngagement(pca, {
        stakeholderId,
        engagementdate: new Date(date).toISOString(),
        type,
        summary: summary.trim() || null,
        loggedbyid: loggedBy.aadObjectId,
        loggedbyname: loggedBy.displayName,
      });
      cancelAdd();
      reload();
    } finally { setSaving(false); }
  };

  const remove = async (e: StakeholderEngagement) => {
    await deleteEngagement(pca, e.dagrun_pm_stakeholderengagementid);
    reload();
  };

  return (
    <div style={{ marginTop: 24, borderTop: '0.5px solid #EAECF0', paddingTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#5A6272', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
          Engagement Log {items.length > 0 && <span style={{ color: '#9198A8', fontWeight: 400 }}>({items.length})</span>}
        </div>
        {!adding && (
          <button type="button" onClick={() => setAdding(true)}
            style={{ background: 'transparent', color: '#0E7490', border: '1px solid #0E7490', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 600, fontFamily: 'var(--font-ui)', cursor: 'pointer' }}
          >+ Add Engagement</button>
        )}
      </div>

      {loading ? (
        <div style={{ fontSize: 12, color: '#9198A8' }}>Loading…</div>
      ) : (
        <>
          {items.length === 0 && !adding && (
            <div style={{ fontSize: 12, color: '#9198A8', fontStyle: 'italic', marginBottom: 10 }}>No engagements logged.</div>
          )}
          {items.map(e => (
            <div key={e.dagrun_pm_stakeholderengagementid} style={{
              display: 'flex', alignItems: 'flex-start', gap: 10,
              padding: '8px 0', borderBottom: '0.5px solid #EAECF0', fontSize: 13,
            }}>
              <span style={{ fontSize: 10, color: '#9198A8', fontFamily: 'var(--font-mono)', minWidth: 70, marginTop: 2 }}>
                {e.dagrun_pm_engagementdate ? new Date(e.dagrun_pm_engagementdate).toLocaleDateString() : '—'}
              </span>
              <span style={{
                fontSize: 9, fontWeight: 600, color: '#0A5568', background: '#D4EEF5',
                padding: '2px 6px', borderRadius: 3, whiteSpace: 'nowrap', marginTop: 1,
              }}>{e.dagrun_pm_type != null ? ENGAGE_TYPE_LABEL[e.dagrun_pm_type] : 'Other'}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: '#0F1117', whiteSpace: 'pre-wrap' }}>{e.dagrun_pm_summary ?? <Em />}</div>
                <div style={{ fontSize: 10, color: '#9198A8', marginTop: 2 }}>by {e.dagrun_pm_loggedbyname ?? '—'}</div>
              </div>
              <button type="button" onClick={() => remove(e)} aria-label="Delete"
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#9198A8', fontSize: 14 }}
                onMouseEnter={ev => (ev.currentTarget.style.color = '#DC2626')}
                onMouseLeave={ev => (ev.currentTarget.style.color = '#9198A8')}
              >×</button>
            </div>
          ))}

          {adding && (
            <div style={{ marginTop: 10, padding: 10, background: '#F5F6F8', borderRadius: 6 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                <div>
                  <label style={BM_LABEL}>Date</label>
                  <input type="date" value={date} onChange={e => setDate(e.target.value)} style={BM_INPUT} />
                </div>
                <div>
                  <label style={BM_LABEL}>Type</label>
                  <select value={type} onChange={e => setType(Number(e.target.value))} style={BM_INPUT}>
                    {[EngagementType.Meeting, EngagementType.Email, EngagementType.Call,
                      EngagementType.Report, EngagementType.Other].map(v =>
                      <option key={v} value={v}>{ENGAGE_TYPE_LABEL[v]}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={BM_LABEL}>Summary</label>
                <AutoSizingTextarea value={summary} onChange={setSummary} minHeight={60} rows={2} />
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={BM_LABEL}>Logged By</label>
                <PeoplePicker pca={pca} value={loggedBy} onChange={setLoggedBy} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
                <button type="button" onClick={cancelAdd} disabled={saving}
                  style={{ background: 'transparent', border: '1px solid #DDE0E6', color: '#5A6272', padding: '4px 10px', fontSize: 12, borderRadius: 4, cursor: 'pointer' }}
                >Cancel</button>
                <button type="button" onClick={save} disabled={saving || !loggedBy}
                  style={{ background: '#0E7490', color: '#fff', border: 'none', padding: '4px 10px', fontSize: 12, fontWeight: 600, borderRadius: 4, cursor: saving || !loggedBy ? 'not-allowed' : 'pointer', opacity: saving || !loggedBy ? 0.6 : 1 }}
                >{saving ? 'Saving…' : 'Add Engagement'}</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
